import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { StatusBadge } from "../../components/common/StatusBadge";
import { CashSettlementService } from "../../services";
import { useAuth } from "../../store/AuthContext";
import { currency, dateTime } from "../../utils/format";
import { businessRange, currentBusinessDate } from "../../utils/businessDay";
import { businessDateTimeValue, sriLankaYear } from "../../utils/sriLankaTime";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Eye,
  Loader2,
  Printer,
  RefreshCw,
  RotateCcw,
  Search,
  Store,
  Wallet,
  X,
  XCircle,
} from "lucide-react";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const VIEW = {
  BAR_OWNER: "BAR_OWNER",
  RESTAURANT_OWNER: "RESTAURANT_OWNER",
  RESTAURANT_CASH: "RESTAURANT_CASH",
  ALL: "ALL",
};

const PRIMARY_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-navy-800 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-navy-900 disabled:cursor-not-allowed disabled:opacity-60";

const ACCENT_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg bg-gold-500 px-5 py-2.5 text-sm font-semibold text-navy-900 transition hover:bg-gold-400 disabled:cursor-not-allowed disabled:opacity-60";

const OUTLINE_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60";

const money = (value) => currency(Number(value || 0));

const getInitialFilters = (businessDate = currentBusinessDate()) => {
  return {
    fromDate: businessDate,
    toDate: businessDate,
    status: "ALL",
    search: "",
  };
};

const settlementTypeLabel = (type) => {
  if (type === "BAR_OWNER") {
    return "Bar → Owner";
  }

  if (type === "RESTAURANT_CASH") {
    return "Bar → Restaurant";
  }

  if (type === "RESTAURANT_OWNER") {
    return "Restaurant → Owner";
  }

  return type || "-";
};

const viewLabel = (view) => {
  if (view === VIEW.BAR_OWNER) {
    return "Bar Payments";
  }

  if (view === VIEW.RESTAURANT_OWNER) {
    return "Restaurant Payments";
  }

  if (view === VIEW.RESTAURANT_CASH) {
    return "Bar → Restaurant";
  }

  return "All Payments";
};

const isSystemStartDate = (value) => {
  if (!value) {
    return true;
  }

  const year = sriLankaYear(value);
  return year != null && year <= 2020;
};

const lastPaymentLabel = (value) => {
  if (isSystemStartDate(value)) {
    return "No previous payment";
  }

  return dateTime(value);
};

const safeDateTime = (value) => {
  if (!value) {
    return "-";
  }

  return dateTime(value);
};

const isWithinBusinessDateRange = (value, fromDate, toDate) => {
  if (!value) {
    return false;
  }

  if (!fromDate && !toDate) {
    return true;
  }

  const recordDateTime = businessDateTimeValue(value);

  const effectiveFromDate = fromDate || toDate;
  const effectiveToDate = toDate || fromDate;

  const { fromDateTime, toDateTime } = businessRange(
    effectiveFromDate,
    effectiveToDate
  );

  return (
    recordDateTime >= businessDateTimeValue(fromDateTime) &&
    recordDateTime < businessDateTimeValue(toDateTime)
  );
};

const isOwnerVisibleSettlementType = (settlementType) =>
  settlementType === "BAR_OWNER" || settlementType === "RESTAURANT_OWNER";

const canActOnSettlement = (row) =>
  row.settlementType === "BAR_OWNER" ||
  row.settlementType === "RESTAURANT_OWNER";

const totalAmount = (rows) =>
  rows.reduce((sum, row) => sum + Number(row.paidAmount || 0), 0);

const filterByStatus = (rows, status) =>
  rows.filter((row) => row.status === status);

const getDialogIcon = (type) => {
  if (type === "success") {
    return CheckCircle2;
  }

  if (type === "error") {
    return XCircle;
  }

  if (type === "warning") {
    return AlertTriangle;
  }

  return Clock3;
};

const getDialogIconClass = (type) => {
  if (type === "success") {
    return "text-emerald-600";
  }

  if (type === "error") {
    return "text-rose-600";
  }

  if (type === "warning") {
    return "text-amber-600";
  }

  return "text-navy-700";
};

const Dialog = ({ dialog, loading, onClose }) => {
  if (!dialog.open) {
    return null;
  }

  const Icon = getDialogIcon(dialog.type);
  const iconClass = getDialogIconClass(dialog.type);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 p-0 sm:items-center sm:p-4">
      <div className="max-h-[92vh] w-full overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-w-2xl sm:rounded-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900">
              {dialog.title}
            </h3>
            {dialog.subtitle && (
              <p className="mt-1 text-sm text-slate-500">{dialog.subtitle}</p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[65vh] overflow-y-auto p-5">
          {dialog.body || (
            <div className="flex items-start gap-3">
              <Icon className={`mt-0.5 h-6 w-6 ${iconClass}`} />
              <p className="text-sm leading-6 text-slate-700">
                {dialog.message}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-100 p-5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className={OUTLINE_BUTTON_CLASS}
          >
            {dialog.cancelLabel || "Close"}
          </button>

          {dialog.onConfirm && (
            <button
              type="button"
              onClick={dialog.onConfirm}
              disabled={loading}
              className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300 ${
                dialog.danger
                  ? "bg-rose-600 hover:bg-rose-700"
                  : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {dialog.confirmLabel || "Confirm"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, bold }) => (
  <div className="grid grid-cols-1 gap-1 rounded-lg border border-slate-100 bg-slate-50 p-3 sm:grid-cols-[170px_1fr] sm:items-center">
    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </span>

    <span
      className={
        bold
          ? "font-display text-base font-semibold text-slate-950"
          : "text-sm text-slate-800"
      }
    >
      {value || "-"}
    </span>
  </div>
);

const NoteForm = ({
  label,
  placeholder,
  required = false,
  danger = false,
  onSubmit,
}) => {
  const [value, setValue] = useState("");

  const canSubmit = !required || value.trim();

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-slate-700">{label}</label>
        <textarea
          value={value}
          onChange={(event) => setValue(event.target.value)}
          rows={3}
          className={`mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none ${
            danger
              ? "focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
              : "focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          }`}
          placeholder={placeholder}
        />
      </div>

      <button
        type="button"
        onClick={() => onSubmit(value.trim())}
        disabled={!canSubmit}
        className={`w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300 ${
          danger
            ? "bg-rose-600 hover:bg-rose-700"
            : "bg-emerald-600 hover:bg-emerald-700"
        }`}
      >
        Continue
      </button>
    </div>
  );
};

const Pagination = ({
  totalItems,
  page,
  pageSize,
  setPage,
  setPageSize,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = totalItems === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(totalItems, (page + 1) * pageSize);

  const canPrevious = page > 0;
  const canNext = page < totalPages - 1;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <span className="text-slate-500">Rows per page</span>

        <select
          value={pageSize}
          onChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>

        <span className="text-slate-500">
          {start}–{end} of {totalItems}
        </span>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setPage(0)}
          disabled={!canPrevious}
          className="rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          «
        </button>

        <button
          type="button"
          onClick={() => setPage((prev) => Math.max(0, prev - 1))}
          disabled={!canPrevious}
          className="rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          ‹
        </button>

        <span className="px-2 text-slate-700">
          Page {page + 1} / {totalPages}
        </span>

        <button
          type="button"
          onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
          disabled={!canNext}
          className="rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          ›
        </button>

        <button
          type="button"
          onClick={() => setPage(totalPages - 1)}
          disabled={!canNext}
          className="rounded-lg border border-slate-200 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          »
        </button>
      </div>
    </div>
  );
};

const SectionButton = ({ active, icon: Icon, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold transition ${
      active
        ? "bms-btn-accent"
        : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
    }`}
  >
    <Icon className="h-4 w-4" />
    {children}
  </button>
);

export const AdminOwnerSettlementsPage = ({ embedded = false, section = "history" }) => {
  const [searchParams] = useSearchParams();
  const businessDate = searchParams.get("businessDate") || currentBusinessDate();
  const { user } = useAuth();
  const previousBusinessDate = useRef(businessDate);

  const isOwnerUser = user?.roleName === "OWNER";
  const isAdminUser = user?.roleName === "ADMIN";

  const [activeView, setActiveView] = useState(() => embedded ? VIEW.ALL : VIEW.BAR_OWNER);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingKey, setActionLoadingKey] = useState("");

  const [filters, setFilters] = useState(() => getInitialFilters(businessDate));

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [dialog, setDialog] = useState({
    open: false,
  });

  const closeDialog = () => {
    if (actionLoadingKey) {
      return;
    }

    setDialog({ open: false });
  };

  const showMessage = (title, message, type = "info") => {
    setDialog({
      open: true,
      title,
      message,
      type,
      cancelLabel: "OK",
    });
  };

  const loadSettlements = useCallback(async () => {
    setLoading(true);

    try {
      const data = await CashSettlementService.adminList(businessDate);
      setSettlements(data || []);
    } catch (error) {
      showMessage(
        "Unable to load settlements",
        error?.response?.data?.message ||
          error?.message ||
          "Please refresh and try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [businessDate]);

  useEffect(() => {
    loadSettlements();
  }, [loadSettlements]);

  useEffect(() => {
    const previous = previousBusinessDate.current;
    if (filters.fromDate === previous && filters.toDate === previous) {
      setFilters((current) => ({ ...current, fromDate: businessDate, toDate: businessDate }));
    }
    previousBusinessDate.current = businessDate;
  }, [businessDate, filters.fromDate, filters.toDate]);

  useEffect(() => {
    if (isOwnerUser && activeView === VIEW.RESTAURANT_CASH) {
      setActiveView(VIEW.BAR_OWNER);
    }
  }, [isOwnerUser, activeView]);

  const visibleRowsForRole = useMemo(() => {
    return (settlements || []).filter((row) => {
      if (!isOwnerUser) {
        return true;
      }

      return isOwnerVisibleSettlementType(row.settlementType);
    });
  }, [settlements, isOwnerUser]);

  const dateFilteredRows = useMemo(() => {
    return visibleRowsForRole.filter((row) =>
      isWithinBusinessDateRange(row.requestedAt, filters.fromDate, filters.toDate)
    );
  }, [visibleRowsForRole, filters.fromDate, filters.toDate]);

  const barOwnerRows = useMemo(
    () => dateFilteredRows.filter((row) => row.settlementType === "BAR_OWNER"),
    [dateFilteredRows]
  );

  const restaurantOwnerRows = useMemo(
    () =>
      dateFilteredRows.filter(
        (row) => row.settlementType === "RESTAURANT_OWNER"
      ),
    [dateFilteredRows]
  );

  const restaurantCashRows = useMemo(
    () =>
      dateFilteredRows.filter(
        (row) => row.settlementType === "RESTAURANT_CASH"
      ),
    [dateFilteredRows]
  );

  const filteredRows = useMemo(() => {
    const search = filters.search.trim().toLowerCase();

    return dateFilteredRows.filter((row) => {
      if (activeView !== VIEW.ALL && row.settlementType !== activeView) {
        return false;
      }

      if (filters.status !== "ALL" && row.status !== filters.status) {
        return false;
      }

      const searchable = [
        row.cashierFullName,
        row.cashierUsername,
        row.settlementType,
        row.status,
        row.businessDate,
        row.cashierNote,
        row.adminNote,
        row.reverseReason,
        row.paidAmount,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return search === "" || searchable.includes(search);
    });
  }, [dateFilteredRows, filters.status, filters.search, activeView]);

  useEffect(() => {
    setPage(0);
  }, [filters, activeView]);

  const pagedRows = useMemo(() => {
    const start = page * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const barPendingRows = filterByStatus(barOwnerRows, "PENDING");
  const barConfirmedRows = filterByStatus(barOwnerRows, "CONFIRMED");

  const restaurantPendingRows = filterByStatus(restaurantOwnerRows, "PENDING");
  const restaurantConfirmedRows = filterByStatus(
    restaurantOwnerRows,
    "CONFIRMED"
  );

  const restaurantCashPendingRows = filterByStatus(restaurantCashRows, "PENDING");

  const openDetails = (row) => {
    setDialog({
      open: true,
      title: settlementTypeLabel(row.settlementType),
      subtitle: `Requested by ${
        row.cashierFullName || row.cashierUsername || "-"
      }`,
      body: (
        <div className="space-y-3 text-sm">
          <Row
            label="Requested Date"
            value={safeDateTime(row.requestedAt)}
          />
          <Row
            label="Cashier/User"
            value={row.cashierFullName || row.cashierUsername || "-"}
          />
          <Row label="Type" value={settlementTypeLabel(row.settlementType)} />
          <Row label="Amount" value={money(row.paidAmount)} bold />
          <Row label="Status" value={<StatusBadge value={row.status} />} />

          {row.businessDate ? (
            <Row label="Business Date" value={row.businessDate} />
          ) : (
            <Row
              label="Last Payment Date"
              value={lastPaymentLabel(row.periodStart)}
            />
          )}

          {row.periodStart && (
            <Row label="Period From" value={safeDateTime(row.periodStart)} />
          )}

          {row.periodEnd && (
            <Row label="Period To" value={safeDateTime(row.periodEnd)} />
          )}

          {row.settlementType === "BAR_OWNER" && (
            <>
              <Row
                label="Bar Cash Sales"
                value={money(row.barCashSalesAmount)}
              />
              <Row
                label="Bar Expenses"
                value={money(row.barExpenseAmount)}
              />
            </>
          )}

          {row.settlementType === "RESTAURANT_OWNER" && (
            <>
              <Row
                label="Restaurant Cash Received"
                value={money(row.restaurantCashAmount)}
              />
              <Row
                label="Restaurant Expenses"
                value={money(row.restaurantExpenseAmount)}
              />
            </>
          )}

          {row.settlementType === "RESTAURANT_CASH" && (
            <Row
              label="Restaurant Cash"
              value={money(row.restaurantCashAmount || row.paidAmount)}
            />
          )}

          {row.cashierNote && (
            <Row label="Request Note" value={row.cashierNote} />
          )}

          {row.confirmedByName && (
            <Row label="Confirmed By" value={row.confirmedByName} />
          )}

          {row.confirmedAt && (
            <Row label="Confirmed At" value={safeDateTime(row.confirmedAt)} />
          )}

          {row.rejectedByName && (
            <Row label="Rejected By" value={row.rejectedByName} />
          )}

          {row.rejectedAt && (
            <Row label="Rejected At" value={safeDateTime(row.rejectedAt)} />
          )}

          {row.adminNote && (
            <Row label="Admin / Owner Note" value={row.adminNote} />
          )}

          {row.reverseReason && (
            <Row label="Reverse Reason" value={row.reverseReason} />
          )}

          {row.settlementType === "RESTAURANT_CASH" && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Bar → Restaurant settlement approval must be handled by the
              restaurant user. Admin can view this record only.
            </div>
          )}
        </div>
      ),
      cancelLabel: "Close",
    });
  };

  const confirmSettlement = (row) => {
    if (!canActOnSettlement(row)) {
      showMessage(
        "Action not allowed",
        "Bar → Restaurant payments must be approved by the restaurant user. You can view this record only.",
        "warning"
      );
      return;
    }

    setDialog({
      open: true,
      title: "Confirm Payment",
      subtitle: "Confirm only after the payment was received.",
      body: (
        <NoteForm
          label="Note optional"
          placeholder="Example: Payment received"
          onSubmit={async (note) => {
            setActionLoadingKey(`confirm-${row.settlementId}`);

            try {
              await CashSettlementService.confirm(row.settlementId, note);

              setDialog({ open: false });
              showMessage(
                "Payment confirmed",
                "The payment was confirmed successfully.",
                "success"
              );

              await loadSettlements();
            } catch (error) {
              showMessage(
                "Confirm failed",
                error?.response?.data?.message ||
                  error?.message ||
                  "Could not confirm payment.",
                "error"
              );
            } finally {
              setActionLoadingKey("");
            }
          }}
        />
      ),
      cancelLabel: "Close",
    });
  };

  const rejectSettlement = (row) => {
    if (!canActOnSettlement(row)) {
      showMessage(
        "Action not allowed",
        "Bar → Restaurant payments must be approved by the restaurant user. You can view this record only.",
        "warning"
      );
      return;
    }

    setDialog({
      open: true,
      title: "Reject Payment",
      subtitle: "Reject if the payment was not received or is invalid.",
      danger: true,
      body: (
        <NoteForm
          label="Reject reason"
          placeholder="Reason for rejecting"
          required
          danger
          onSubmit={async (reason) => {
            setActionLoadingKey(`reject-${row.settlementId}`);

            try {
              await CashSettlementService.reject(row.settlementId, reason);

              setDialog({ open: false });
              showMessage(
                "Payment rejected",
                "The payment was rejected successfully.",
                "success"
              );

              await loadSettlements();
            } catch (error) {
              showMessage(
                "Reject failed",
                error?.response?.data?.message ||
                  error?.message ||
                  "Could not reject payment.",
                "error"
              );
            } finally {
              setActionLoadingKey("");
            }
          }}
        />
      ),
      cancelLabel: "Close",
    });
  };

  const reverseSettlement = (row) => {
    if (!isAdminUser || row.status !== "CONFIRMED") return;
    setDialog({
      open: true,
      title: "Reverse Settlement",
      subtitle: "Provide the reason for reversing this confirmed settlement.",
      danger: true,
      body: <NoteForm label="Reversal reason" placeholder="Reason for reversal" required danger onSubmit={async (reason) => {
        setActionLoadingKey(`reverse-${row.settlementId}`);
        try {
          await CashSettlementService.reverse(row.settlementId, reason);
          setDialog({ open: false });
          showMessage("Settlement reversed", "The settlement was reversed successfully.", "success");
          await loadSettlements();
        } catch (error) {
          showMessage("Reversal failed", error?.response?.data?.message || error?.message || "Could not reverse settlement.", "error");
        } finally {
          setActionLoadingKey("");
        }
      }} />,
      cancelLabel: "Cancel",
    });
  };

  const resetToToday = () => {
    const today = currentBusinessDate();

    setFilters((prev) => ({
      ...prev,
      fromDate: today,
      toDate: today,
    }));
  };

  return (
    <div>
      {!embedded && <PageHeader
        title="Settlement Payments"
        subtitle={
          isOwnerUser
            ? "View Bar → Owner and Restaurant → Owner payments separately."
            : "Review Bar → Owner, Restaurant → Owner, and Bar → Restaurant payment records separately."
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadSettlements}
              disabled={loading}
              className={PRIMARY_BUTTON_CLASS}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </button>

            <button
              type="button"
              onClick={() => window.print()}
              className={ACCENT_BUTTON_CLASS}
            >
              <Printer className="h-4 w-4" />
              Print / PDF
            </button>
          </div>
        }
      />}
      {embedded && <div className="mb-4 flex justify-end gap-2">
        <button type="button" onClick={loadSettlements} disabled={loading} className={OUTLINE_BUTTON_CLASS}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh</button>
        <button type="button" onClick={() => window.print()} className={ACCENT_BUTTON_CLASS}><Printer className="h-4 w-4" />Print / PDF</button>
      </div>}

      {embedded && section === "overview" && <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
          <span className="font-semibold text-slate-800">Business Date <span className="ml-1 font-normal text-slate-600">{businessDate}</span></span>
          <span className="text-slate-600">Pending Owner Settlements <strong className="ml-1 text-slate-900">{loading ? "…" : restaurantPendingRows.length + barPendingRows.length}</strong></span>
          <span className="text-slate-600">BAR Settlement Amount <strong className="ml-1 text-slate-900">{loading ? "…" : money(totalAmount(barPendingRows))}</strong></span>
          <span className="text-slate-600">Restaurant Settlement Amount <strong className="ml-1 text-slate-900">{loading ? "…" : money(totalAmount(restaurantPendingRows))}</strong></span>
        </div>
        {(barPendingRows.length + restaurantPendingRows.length) > 0 && <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900">{barPendingRows.length + restaurantPendingRows.length} pending owner settlement request(s) need review. Open Settlements to confirm or reject them.</div>}
      </div>}

      {(!embedded || section !== "overview") && <>
      {!embedded && <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Bar Pending"
          value={loading ? "…" : money(totalAmount(barPendingRows))}
          icon="Wallet"
          tone="gold"
        />

        <StatCard
          label="Bar Confirmed"
          value={loading ? "…" : money(totalAmount(barConfirmedRows))}
          icon="BadgeDollarSign"
          tone="emerald"
        />

        <StatCard
          label="Restaurant Pending"
          value={loading ? "…" : money(totalAmount(restaurantPendingRows))}
          icon="Store"
          tone="sky"
        />

        <StatCard
          label="Restaurant Confirmed"
          value={loading ? "…" : money(totalAmount(restaurantConfirmedRows))}
          icon="Receipt"
          tone="navy"
        />
      </div>}

      {!isOwnerUser && restaurantCashPendingRows.length > 0 && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          There are {restaurantCashPendingRows.length} pending Bar → Restaurant
          payment request(s). These are view-only here and must be confirmed by
          the restaurant user.
        </div>
      )}

      {embedded ? <div className="mb-3 flex items-center gap-2">
        <label htmlFor="settlement-type-filter" className="text-sm font-medium text-slate-600">Type</label>
        <select id="settlement-type-filter" value={activeView} onChange={(event) => setActiveView(event.target.value)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value={VIEW.ALL}>All Types</option><option value={VIEW.BAR_OWNER}>BAR → Owner</option><option value={VIEW.RESTAURANT_OWNER}>Restaurant → Owner</option>{!isOwnerUser && <option value={VIEW.RESTAURANT_CASH}>BAR → Restaurant</option>}
        </select>
      </div> : <div className="mt-6 flex gap-2 overflow-x-auto pb-1">
        <SectionButton
          active={activeView === VIEW.BAR_OWNER}
          icon={Wallet}
          onClick={() => setActiveView(VIEW.BAR_OWNER)}
        >
          Bar Payments
        </SectionButton>

        <SectionButton
          active={activeView === VIEW.RESTAURANT_OWNER}
          icon={Store}
          onClick={() => setActiveView(VIEW.RESTAURANT_OWNER)}
        >
          Restaurant Payments
        </SectionButton>

        {!isOwnerUser && (
          <SectionButton
            active={activeView === VIEW.RESTAURANT_CASH}
            icon={Store}
            onClick={() => setActiveView(VIEW.RESTAURANT_CASH)}
          >
            Bar → Restaurant
          </SectionButton>
        )}

        <SectionButton
          active={activeView === VIEW.ALL}
          icon={Clock3}
          onClick={() => setActiveView(VIEW.ALL)}
        >
          All History
        </SectionButton>
      </div>}

      <div className="bms-card p-5 mt-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold">
              {viewLabel(activeView)}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {activeView === VIEW.BAR_OWNER &&
                "Payments made from bar cashier to owner."}
              {activeView === VIEW.RESTAURANT_OWNER &&
                "Payments made from restaurant user to owner."}
              {activeView === VIEW.RESTAURANT_CASH &&
                "Payments made from bar cashier to restaurant. This is view-only for admin."}
              {activeView === VIEW.ALL &&
                "All settlement payment records for the selected date range."}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-[145px_145px_170px_240px_auto]">
            <input
              type="date"
              value={filters.fromDate}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  fromDate: event.target.value,
                }))
              }
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
            />

            <input
              type="date"
              value={filters.toDate}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  toDate: event.target.value,
                }))
              }
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
            />

            <select
              value={filters.status}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  status: event.target.value,
                }))
              }
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none"
            >
              <option value="ALL">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="REJECTED">Rejected</option>
              <option value="REVERSED">Reversed</option>
            </select>

            <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
              <Search className="h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    search: event.target.value,
                  }))
                }
                className="w-full bg-transparent text-sm outline-none"
                placeholder="Search"
              />
            </div>

            <button
              type="button"
              onClick={resetToToday}
              className={OUTLINE_BUTTON_CLASS}
            >
              Today
            </button>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-500">
                  Requested Date
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">
                  Cashier/User
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">
                  Type
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">
                  Period / Date
                </th>
                <th className="px-4 py-3 text-right font-medium text-slate-500">
                  Amount
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">
                  Status
                </th>
                <th className="px-4 py-3 text-right font-medium text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {pagedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-8 text-center text-slate-500"
                  >
                    No payment records found for the selected date.
                  </td>
                </tr>
              ) : (
                pagedRows.map((row) => {
                  const canAct = canActOnSettlement(row);
                  const restaurantApprovalPending =
                    row.settlementType === "RESTAURANT_CASH" &&
                    row.status === "PENDING";

                  return (
                    <tr key={row.settlementId}>
                      <td className="px-4 py-3 text-slate-700">
                        {safeDateTime(row.requestedAt)}
                      </td>

                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">
                          {row.cashierFullName || row.cashierUsername || "-"}
                        </div>
                        <div className="text-xs text-slate-500">
                          {row.cashierUsername || "-"}
                        </div>
                      </td>

                      <td className="px-4 py-3 font-medium text-slate-900">
                        {settlementTypeLabel(row.settlementType)}
                      </td>

                      <td className="px-4 py-3 text-slate-700">
                        {row.businessDate ? (
                          <span>Business Date: {row.businessDate}</span>
                        ) : (
                          <span>
                            Last Payment: {lastPaymentLabel(row.periodStart)}
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-right font-semibold">
                        {money(row.paidAmount)}
                      </td>

                      <td className="px-4 py-3">
                        <StatusBadge value={row.status} />
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openDetails(row)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            <Eye className="h-3 w-3" />
                            View
                          </button>

                          {restaurantApprovalPending && (
                            <span className="inline-flex items-center rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700">
                              Awaiting Restaurant
                            </span>
                          )}

                          {canAct && row.status === "PENDING" && (
                            <>
                              <button
                                type="button"
                                onClick={() => confirmSettlement(row)}
                                className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                              >
                                <CheckCircle2 className="h-3 w-3" />
                                Confirm
                              </button>

                              <button
                                type="button"
                                onClick={() => rejectSettlement(row)}
                                className="inline-flex items-center gap-1 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                              >
                                <XCircle className="h-3 w-3" />
                                Reject
                              </button>
                            </>
                          )}
                          {isAdminUser && row.status === "CONFIRMED" && (
                            <button type="button" onClick={() => reverseSettlement(row)} className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100">
                              <RotateCcw className="h-3 w-3" />Reverse
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          totalItems={filteredRows.length}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </div>
      </>}

      <Dialog
        dialog={dialog}
        loading={Boolean(actionLoadingKey)}
        onClose={closeDialog}
      />
    </div>
  );
};
